/**
 * DocumentValidationModal - Document preview with field verification
 *
 * Features:
 * - Document preview (PDF/image viewer)
 * - Side-by-side field comparison table
 * - Checkbox-based validation
 * - Three actions: Validate, Reject, Cannot Provide
 */

import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  X, CheckCircle, XCircle, AlertTriangle,
  Loader2, Edit2, FileText, Download, RotateCw, ZoomIn, ZoomOut
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { VALIDATION_FIELDS } from '../../config/complianceRules.js';
import { documentValidationAPI, documentAPI } from '../../api.js';

/**
 * Get validation fields config for a document type
 */
function getValidationFieldsConfig(documentType) {
  // Map folder document types to validation field keys
  const mappings = {
    'VEHICLE_REGISTRATION': VALIDATION_FIELDS.VEHICLE_REGISTRATION,
    'CARTE_GRISE': VALIDATION_FIELDS.VEHICLE_REGISTRATION,
    'TECHNICAL_INSPECTION': VALIDATION_FIELDS.TECHNICAL_INSPECTION,
    'CONTROLE_TECHNIQUE': VALIDATION_FIELDS.TECHNICAL_INSPECTION,
    'INSURANCE_CARD': VALIDATION_FIELDS.INSURANCE_CARD,
    'ATTESTATION_ASSURANCE': VALIDATION_FIELDS.INSURANCE_CARD,
    'CARTE_VERTE': VALIDATION_FIELDS.INSURANCE_CARD,
    'DRIVING_LICENCE': VALIDATION_FIELDS.DRIVING_LICENCE,
    'PERMIS_CONDUIRE': VALIDATION_FIELDS.DRIVING_LICENCE,
    'CERTIFICATION': VALIDATION_FIELDS.DRIVER_CERTIFICATION,
    'FIMO': VALIDATION_FIELDS.DRIVER_CERTIFICATION,
    'FCO': VALIDATION_FIELDS.DRIVER_CERTIFICATION,
    'ADR': VALIDATION_FIELDS.DRIVER_CERTIFICATION,
  };

  // Try to match document type
  const upperType = (documentType || '').toUpperCase().replace(/[^A-Z_]/g, '_');
  return mappings[upperType] || null;
}

/**
 * DocumentValidationModal
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - doc: Document object to validate
 * - entityData: Entity data for pre-filling fields
 * - onValidated: (result) => void
 * - onRejectClick: (doc) => void - Opens rejection modal
 * - onCannotProvideClick: (doc) => void - Opens cannot provide modal
 */
export default function DocumentValidationModal({
  isOpen,
  onClose,
  doc,
  entityData = {},
  onValidated,
  onRejectClick,
  onCannotProvideClick,
  viewOnly = false
}) {
  const { t } = useTranslation();
  // Loading state removed - unused
  const [validating, setValidating] = useState(false);
  const [notes, setNotes] = useState('');
  const [fieldStates, setFieldStates] = useState({});
  const [fieldValues, setFieldValues] = useState({});
  const [editingField, setEditingField] = useState(null);

  // Blob URL for authenticated document viewing
  const [blobUrl, setBlobUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);

  // Load blob URL for PDF/image viewing when modal opens
  useEffect(() => {
    let currentBlobUrl = null;

    if (!isOpen || !doc?.id) {
      setBlobUrl(null);
      setPreviewError(null);
      setLoadingPreview(false);
      setRotation(0);
      setZoom(1);
      return;
    }

    const loadDocumentBlob = async () => {
      setLoadingPreview(true);
      setPreviewError(null);
      try {
        const url = await documentAPI.getBlobUrl(doc.id, doc.mime_type);
        currentBlobUrl = url;
        setBlobUrl(url);
      } catch (err) {
        console.error('Failed to load document preview:', err);
        setPreviewError(err.message || 'Failed to load preview');
      } finally {
        setLoadingPreview(false);
      }
    };

    loadDocumentBlob();

    // Cleanup on unmount or document change
    return () => {
      if (currentBlobUrl) {
        window.URL.revokeObjectURL(currentBlobUrl);
      }
    };
  }, [isOpen, doc?.id, doc?.mime_type]);

  // Get validation fields for this document type
  const validationFields = useMemo(() => {
    return getValidationFieldsConfig(doc?.document_type) || [];
  }, [doc?.document_type]);

  // Initialize field states from existing data
  useEffect(() => {
    if (doc && validationFields.length > 0) {
      const initialStates = {};
      const initialValues = {};

      // Parse existing declared/verified data
      let declaredData = {};
      let verifiedData = {};
      try {
        declaredData = doc.declared_data ? JSON.parse(doc.declared_data) : {};
        verifiedData = doc.verified_data ? JSON.parse(doc.verified_data) : {};
      } catch (e) {
        console.error('Failed to parse doc data:', e);
      }

      // Compute derived entity fields for fallback
      const enrichedEntity = { ...entityData };
      // DRIVING_LICENCE: holder_name from first_name + last_name
      if (entityData.first_name || entityData.last_name) {
        enrichedEntity._holder_name = [entityData.first_name, entityData.last_name].filter(Boolean).join(' ');
      }
      // VEHICLE_REGISTRATION: owner_name from entity field
      if (entityData.owner_name) {
        enrichedEntity._owner_name = entityData.owner_name;
      }
      const catParts = [];
      if (entityData.license_category_b) catParts.push('B');
      if (entityData.license_category_c) catParts.push('C');
      if (entityData.license_category_d) catParts.push('D');
      if (entityData.license_category_ce) catParts.push('CE');
      if (catParts.length) enrichedEntity._categories_display = catParts.join(', ');

      validationFields.forEach(field => {
        // Get declared value (from OCR or entity field)
        let declaredValue = declaredData[field.code];
        if (!declaredValue && field.entityField) {
          declaredValue = enrichedEntity[field.entityField];
        }

        // Get verified value if exists
        const verifiedValue = verifiedData[field.code];

        initialValues[field.code] = {
          declared: declaredValue || '',
          verified: verifiedValue || declaredValue || ''
        };

        // If already verified, mark as checked
        initialStates[field.code] = !!verifiedValue;
      });

      setFieldStates(initialStates);
      setFieldValues(initialValues);
    }
  }, [doc, validationFields, entityData]);

  // Check if all required fields are verified
  const allFieldsVerified = useMemo(() => {
    if (validationFields.length === 0) return true;
    return validationFields.every(f => fieldStates[f.code] === true);
  }, [validationFields, fieldStates]);

  // Handle field checkbox toggle
  const toggleFieldVerified = (fieldCode) => {
    setFieldStates(prev => ({
      ...prev,
      [fieldCode]: !prev[fieldCode]
    }));
  };

  // Handle field value edit - reserved for future use
  // const updateFieldValue = (fieldCode, value) => {
  //   setFieldValues(prev => ({
  //     ...prev,
  //     [fieldCode]: {
  //       ...prev[fieldCode],
  //       verified: value
  //     }
  //   }));
  //   setEditingField(null);
  // };

  // Handle validate action
  const handleValidate = async () => {
    if (!allFieldsVerified) {
      alert(t('documents.validation.errors.fieldsNotVerified'));
      return;
    }

    setValidating(true);
    try {
      // Prepare verified data
      const verifiedData = {};
      validationFields.forEach(field => {
        verifiedData[field.code] = fieldValues[field.code]?.verified || '';
      });

      const result = await documentValidationAPI.validate(doc.id, {
        notes,
        verified_data: verifiedData
      });

      if (result.ok) {
        onValidated?.(result.data);
        onClose();
      } else {
        alert(result.error?.message || t('documents.validation.errors.validateFailed'));
      }
    } catch (err) {
      console.error('Validation error:', err);
      alert(t('documents.validation.errors.validateFailedDetail', { message: err.message }));
    } finally {
      setValidating(false);
    }
  };

  if (!isOpen || !doc) {
    return null;
  }

  // Handle download
  const handleDownload = () => {
    documentAPI.download(doc.id, doc.original_filename || doc.filename);
  };

  // Use portal to render at document.body level, escaping any stacking context
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="doc-validation-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            {viewOnly ? t('documents.validation.previewTitle') : t('documents.validation.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left: Document Preview */}
          <div className={`${viewOnly ? 'w-full' : 'w-1/2 border-r'} flex flex-col`}>
            <div className="p-3 border-b bg-gray-50 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600 truncate">
                {doc.original_filename || doc.filename}
              </span>
              <div className="flex items-center gap-1.5">
                <div className="flex items-center bg-gray-100 rounded-lg px-1 py-0.5 gap-0.5">
                  <button
                    onClick={() => setZoom(z => Math.max(z - 0.25, 0.25))}
                    className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded"
                    title={t('documents.validation.zoomOut')}
                  >
                    <ZoomOut className="w-3.5 h-3.5" />
                  </button>
                  <span className="text-xs text-gray-500 w-9 text-center font-medium">{Math.round(zoom * 100)}%</span>
                  <button
                    onClick={() => setZoom(z => Math.min(z + 0.25, 3))}
                    className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded"
                    title={t('documents.validation.zoomIn')}
                  >
                    <ZoomIn className="w-3.5 h-3.5" />
                  </button>
                </div>
                <button
                  onClick={() => setRotation(r => (r + 90) % 360)}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
                    rotation !== 0
                      ? 'bg-amber-500 text-white hover:bg-amber-600'
                      : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                  }`}
                  title={t('documents.validation.rotateTitle')}
                >
                  <RotateCw className="w-3.5 h-3.5" />
                  {t('documents.validation.rotate')}
                </button>
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  {t('common.actions.download')}
                </button>
              </div>
            </div>
            <div className="flex-1 bg-gray-100 flex items-center justify-center p-4 overflow-auto">
              {loadingPreview ? (
                <div className="text-center text-gray-500">
                  <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-blue-500" />
                  <p>{t('documents.validation.loadingPreview')}</p>
                </div>
              ) : previewError ? (
                <div className="text-center text-gray-500">
                  <FileText className="w-16 h-16 mx-auto mb-2 text-gray-300" />
                  <p className="text-red-500 mb-2">{previewError}</p>
                  <button
                    onClick={handleDownload}
                    className="text-blue-600 hover:underline flex items-center gap-1 justify-center"
                  >
                    <Download className="w-4 h-4" />
                    {t('documents.validation.downloadFile')}
                  </button>
                </div>
              ) : blobUrl ? (
                doc.mime_type?.startsWith('image/') ? (
                  <div
                    className="transition-transform duration-200 origin-center"
                    style={{ transform: `rotate(${rotation}deg) scale(${zoom})` }}
                  >
                    <img
                      src={blobUrl}
                      alt={doc.original_filename || doc.filename}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ) : doc.mime_type === 'application/pdf' ? (
                  <div
                    className="w-full h-full transition-transform duration-200 origin-center"
                    style={{ transform: `rotate(${rotation}deg) scale(${zoom})`, minHeight: '400px' }}
                  >
                    <iframe
                      src={`${blobUrl}#toolbar=0&navpanes=0`}
                      className="w-full h-full bg-white"
                      style={{ minHeight: '400px' }}
                      title="Document Preview"
                    />
                  </div>
                ) : (
                  <div className="text-center text-gray-500">
                    <FileText className="w-16 h-16 mx-auto mb-2 text-gray-300" />
                    <p>{t('documents.validation.previewUnsupported')}</p>
                    <button
                      onClick={handleDownload}
                      className="text-blue-600 hover:underline flex items-center gap-1 justify-center mt-2"
                    >
                      <Download className="w-4 h-4" />
                      {t('documents.validation.downloadFile')}
                    </button>
                  </div>
                )
              ) : (
                <div className="text-center text-gray-500">
                  <FileText className="w-16 h-16 mx-auto mb-2 text-gray-300" />
                  <p>{t('documents.validation.noPreview')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Right: Field Verification - hidden in viewOnly mode */}
          {!viewOnly && (
          <div className="w-1/2 flex flex-col">
            <div className="p-3 border-b bg-gray-50">
              <span className="text-sm font-medium text-gray-600">
                {t('documents.validation.dataVerification')}
              </span>
            </div>

            <div className="flex-1 overflow-auto p-4">
              {validationFields.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="py-2 text-left font-medium text-gray-600">{t('documents.validation.colField')}</th>
                      <th className="py-2 text-left font-medium text-gray-600">{t('documents.validation.colDeclared')}</th>
                      <th className="py-2 text-center font-medium text-gray-600 w-16">{t('documents.validation.colCompliant')}</th>
                      <th className="py-2 text-center font-medium text-gray-600 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {validationFields.map(field => (
                      <tr key={field.code} className="border-b">
                        <td className="py-2 font-medium text-gray-700">
                          {t(`documents.fields.${field.code}`, { defaultValue: field.label })}
                        </td>
                        <td className="py-2 text-gray-600">
                          {editingField === field.code ? (
                            <input
                              type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
                              value={fieldValues[field.code]?.verified || ''}
                              onChange={(e) => setFieldValues(prev => ({
                                ...prev,
                                [field.code]: { ...prev[field.code], verified: e.target.value }
                              }))}
                              onBlur={() => setEditingField(null)}
                              onKeyDown={(e) => e.key === 'Enter' && setEditingField(null)}
                              className="w-full px-2 py-1 border rounded text-sm"
                              autoFocus
                            />
                          ) : (
                            <span className={fieldStates[field.code] ? 'text-green-700' : ''}>
                              {fieldValues[field.code]?.verified || fieldValues[field.code]?.declared || '-'}
                            </span>
                          )}
                        </td>
                        <td className="py-2 text-center">
                          <input
                            type="checkbox"
                            checked={fieldStates[field.code] || false}
                            onChange={() => toggleFieldVerified(field.code)}
                            className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                          />
                        </td>
                        <td className="py-2 text-center">
                          <button
                            onClick={() => setEditingField(field.code)}
                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                            title={t('documents.validation.editValue')}
                          >
                            <Edit2 className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>{t('documents.validation.noFields')}</p>
                </div>
              )}

              {/* Notes */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  {t('documents.validation.notes')}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('documents.validation.notesPlaceholder')}
                  className="w-full px-3 py-2 border rounded text-sm resize-none"
                  rows={2}
                />
              </div>
            </div>

            {/* Status info */}
            {doc.validation_status === 'pending' && (
              <div className="px-4 py-2 bg-amber-50 border-t border-amber-100 text-sm text-amber-700">
                {t('documents.folderStatus.pending_validation')}
              </div>
            )}
            {doc.validation_status === 'rejected' && (
              <div className="px-4 py-2 bg-red-50 border-t border-red-100 text-sm text-red-700">
                {t('documents.validation.rejectedWithReason', { reason: doc.rejection_reason })}
              </div>
            )}
          </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-end">
          {viewOnly ? (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              {t('common.actions.close')}
            </button>
          ) : (
            <>
              <div className="flex gap-2 mr-auto">
                <button
                  onClick={() => onRejectClick?.(doc)}
                  className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 flex items-center gap-2"
                >
                  <XCircle className="w-4 h-4" />
                  {t('documents.rejection.submit')}
                </button>
                <button
                  onClick={() => onCannotProvideClick?.(doc)}
                  className="px-4 py-2 text-sm bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 flex items-center gap-2"
                >
                  <AlertTriangle className="w-4 h-4" />
                  {t('documents.validation.cannotProvide')}
                </button>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded"
                >
                  {t('common.actions.cancel')}
                </button>
                <button
                  onClick={handleValidate}
                  disabled={validating || !allFieldsVerified}
                  className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {validating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  {t('documents.validation.validate')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    window.document.body
  );
}
